import { useMutation } from "@apollo/client";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AttachFile from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import StopIcon from "@mui/icons-material/Stop";
import { Box, Button, IconButton, Stack, TextField, Tooltip, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { runConcurrently } from "encamp-shared/src/concurrency";
import {
  SCOUT_POSTHOG_EVENTS,
  SCOUT_UPSELL_PLACEMENTS,
} from "encamp-shared/src/instrumentation/events";
import { gql } from "generated-graphql";
import { type DeletePrivateDocumentMutation } from "generated-graphql/graphql";
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { ScoutAttachment, ScoutSubmitHandler } from "../scoutSubmitAttachments";
import type { ScoutFilePart } from "./message/scoutChatMessage";

import { capturePosthog } from "../../../../instrumentation/posthog";
import { scoutHostToRuntime } from "../../../../instrumentation/scoutHost";
import { uploadFileDirectly } from "../../../../util/uploadFile";
import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "../hooks/scoutWriteMode";
import { useScoutWriteAccessControl } from "../hooks/useScoutWriteAccessControl";
import {
  SCOUT_ATTACHMENT_FILE_INPUT_ACCEPT,
  SCOUT_MAX_ATTACHMENTS,
  validateScoutAttachmentFile,
} from "../scoutAttachmentLimits";
import { SCOUT_SUPPORT_ARTICLE } from "../ScoutInfoPanel";
import { ScoutFileDisplay } from "./message/ScoutFileDisplay";
import { useRegisterScoutChatFileUpload } from "./ScoutChatDropzone";
import { ScoutFileThumbnailCard } from "./ScoutFileThumbnailCard";
import { useScoutHostContext } from "./ScoutHostContext";
import { ScoutWriteAccessIndicator } from "./ScoutWriteAccessIndicator";

function isScoutImageMediaType(mediaType: string): boolean {
  return mediaType.toLowerCase().startsWith("image/");
}

function revokePreviewUrl(previewUrl: string | undefined): void {
  if (previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(previewUrl);
  }
}

const INPUT_SCROLLBAR_WIDTH_PX = 12;

function getInputActionRightPaddingPx(
  hasWriteAccessAdornment: boolean,
  isCompact: boolean
): number {
  if (hasWriteAccessAdornment) {
    return isCompact ? 84 : 92;
  }
  return isCompact ? 40 : 44;
}

const CREATE_PRIVATE_DOCUMENT = gql(`
  mutation CreatePrivateDocument($input: CreateScoutPrivateDocumentInput!) {
    createPrivateDocument(input: $input) {
      id
      title
      filename
      fileExtension
      mediaType
      sizeBytes
      pageCount
      ingestionMode
      extractionStatus
      signedDownloadUrl
      thumbnailSignedUrl
    }
  }
`);

const DELETE_PRIVATE_DOCUMENT = gql(`
  mutation DeletePrivateDocument($input: DeleteScoutPrivateDocumentInput!) {
    deleteScoutPrivateDocument(input: $input) {
      deletedPrivateDocumentIds
      failures {
        privateDocumentId
        message
      }
    }
  }
`);

type ScoutAttachmentError = {
  id: string;
  filename: string;
  mediaType: string;
  message: string;
};

type ScoutPrivateDocumentDeleteFailure =
  DeletePrivateDocumentMutation["deleteScoutPrivateDocument"]["failures"][number];

type ScoutPendingUpload = {
  clientId: string;
  filename: string;
  mediaType: string;
  sizeBytes?: number;
  previewUrl?: string;
  localFileUrl?: string;
};

type ScoutUploadTask = {
  file: File;
  clientId: string;
  previewUrl?: string;
  localFileUrl: string;
};

function revokeComposerFileResources(input: { previewUrl?: string; localFileUrl?: string }): void {
  if (input.localFileUrl) {
    revokePreviewUrl(input.localFileUrl);
  }
  if (input.previewUrl && input.previewUrl !== input.localFileUrl) {
    revokePreviewUrl(input.previewUrl);
  }
}

/** Blob URLs are revoked after upload; image attachments can use signedDownloadUrl instead. */
function persistedAttachmentPreviewUrl(previewUrl: string | undefined): string | undefined {
  if (previewUrl?.startsWith("blob:")) {
    return undefined;
  }
  return previewUrl;
}

function resolveScoutAttachmentThumbnailUrl(attachment: {
  thumbnailSignedUrl?: string;
  previewUrl?: string;
  mediaType: string;
  signedDownloadUrl?: string;
}): string | undefined {
  if (attachment.thumbnailSignedUrl) {
    return attachment.thumbnailSignedUrl;
  }
  if (attachment.previewUrl) {
    return attachment.previewUrl;
  }
  if (isScoutImageMediaType(attachment.mediaType)) {
    return attachment.signedDownloadUrl;
  }
  return undefined;
}

export type { ScoutAttachment, ScoutSubmitHandler } from "../scoutSubmitAttachments";

export const ScoutInput = memo(function ScoutThreadInput({
  onSubmit,
  onAbort,
  isSending,
  tenantId,
  conversationId,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  isAborting = false,
  placeholder = "Chat with Scout…",
  compactPlaceholder,
  autoFocus = false,
  lastUserMessage,
  writeMode = SCOUT_WRITE_MODE_GQL.OFF,
  onWriteModeChange,
  hasScoutProAccess = true,
  composerFooter,
}: {
  onSubmit: ScoutSubmitHandler;
  onAbort?: () => void;
  isSending: boolean;
  tenantId?: string;
  conversationId?: string;
  onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
  onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
  isAborting?: boolean;
  placeholder?: string;
  /** Shorter placeholder below the `sm` breakpoint. */
  compactPlaceholder?: string;
  autoFocus?: boolean;
  /**
   * Most recent user message text. ArrowUp on an empty input fills it in so
   * the user can quickly retry after an errored turn without retyping.
   */
  lastUserMessage?: string;
  writeMode?: ScoutWriteModeGql;
  onWriteModeChange?: (next: ScoutWriteModeGql) => void;
  hasScoutProAccess?: boolean;
  /**
   * Optional sub-footer rendered inside the composer chrome below the text
   * field (e.g. popover page-context chip).
   */
  composerFooter?: ReactNode;
}) {
  const theme = useTheme();
  const isCompactComposer = useMediaQuery(theme.breakpoints.down("sm"));
  const [hasContent, setHasContent] = useState(false);
  const [attachments, setAttachments] = useState<ScoutAttachment[]>([]);
  const [pendingUploads, setPendingUploads] = useState<ScoutPendingUpload[]>([]);
  const [attachmentErrors, setAttachmentErrors] = useState<ScoutAttachmentError[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showScoutProUploadUpsell, setShowScoutProUploadUpsell] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [createPrivateDocument] = useMutation(CREATE_PRIVATE_DOCUMENT);
  const [deletePrivateDocumentMutation] = useMutation(DELETE_PRIVATE_DOCUMENT);
  const { host } = useScoutHostContext();

  const handleFileUploadUpsellLearnMoreClick = useCallback(() => {
    capturePosthog(SCOUT_POSTHOG_EVENTS.PRO_UPSELL_LEARN_MORE_CLICKED, {
      host: scoutHostToRuntime(host),
      placement: SCOUT_UPSELL_PLACEMENTS.FILE_UPLOAD_BANNER,
      capability: "file_upload_or_analysis",
    });
  }, [host]);

  const deletePrivateDocuments = useCallback(
    async (privateDocumentIds: string[]) => {
      if (!tenantId || privateDocumentIds.length === 0) {
        return {
          deletedPrivateDocumentIds: [] as string[],
          failures: [] as ScoutPrivateDocumentDeleteFailure[],
        };
      }

      const result = await deletePrivateDocumentMutation({
        variables: {
          input: {
            tenantId,
            privateDocumentIds,
            conversationId: conversationId ?? null,
          },
        },
      });
      const deleteResult = result.data?.deleteScoutPrivateDocument;
      if (!deleteResult) {
        throw new Error("Scout could not remove these attachments.");
      }

      return {
        deletedPrivateDocumentIds: deleteResult.deletedPrivateDocumentIds,
        failures: deleteResult.failures,
      };
    },
    [conversationId, deletePrivateDocumentMutation, tenantId]
  );
  const [removingAttachmentIds, setRemovingAttachmentIds] = useState<Set<string>>(() => new Set());
  const isInputLocked = isSending || isUploading || removingAttachmentIds.size > 0;
  const hasReachedAttachmentLimit = attachments.length >= SCOUT_MAX_ATTACHMENTS;
  const isAttachmentActionDisabled = isInputLocked || hasReachedAttachmentLimit;
  const isAttachButtonDisabled = hasScoutProAccess ? isAttachmentActionDisabled : isInputLocked;
  const isSubmitDisabled = (!hasContent && attachments.length === 0) || isInputLocked || isSending;
  const inputSurfaceColor = theme.palette.background.paper;
  const inputScrollbarThumbColor = theme.palette.scrollbarThumb;
  const { isFeatureEnabled: isWriteAccessFeatureEnabled } = useScoutWriteAccessControl({
    writeMode,
    onWriteModeChange: onWriteModeChange ?? (() => undefined),
    disabled: isSending || isInputLocked,
    hasScoutProAccess,
  });

  const showClearAllAttachments = attachments.length + pendingUploads.length > 1;
  const hasWriteAccessAdornment = isWriteAccessFeatureEnabled && Boolean(onWriteModeChange);
  const inputActionRightPaddingPx = getInputActionRightPaddingPx(
    hasWriteAccessAdornment,
    isCompactComposer
  );
  // While Scout is working the composer is locked and Enter inserts a newline
  // rather than sending, so a "follow-up" prompt would misrepresent what the
  // input can do. Show a working state instead of the idle placeholder copy.
  const idlePlaceholder =
    isCompactComposer && compactPlaceholder ? compactPlaceholder : placeholder;
  const resolvedPlaceholder = isSending ? "Scout is working…" : idlePlaceholder;
  const useExpandedComposerLayout =
    attachments.length > 0 ||
    pendingUploads.length > 0 ||
    attachmentErrors.length > 0 ||
    isUploading;

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const requestScoutProForUploads = useCallback(() => {
    setShowScoutProUploadUpsell(true);
  }, []);

  const clearInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setHasContent(false);
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments((previous) => {
      for (const attachment of previous) {
        revokeComposerFileResources(attachment);
      }
      return [];
    });
    setPendingUploads((previous) => {
      for (const pendingUpload of previous) {
        revokeComposerFileResources(pendingUpload);
      }
      return [];
    });
    setAttachmentErrors([]);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      focusInput();
    }
  }, [autoFocus, focusInput]);

  useEffect(() => {
    if (hasScoutProAccess) {
      setShowScoutProUploadUpsell(false);
    }
  }, [hasScoutProAccess]);

  const uploadAttachments = useCallback(
    async (files: File[]) => {
      if (!hasScoutProAccess) {
        requestScoutProForUploads();
        return;
      }
      if (isInputLocked) {
        return;
      }

      const nextErrors: ScoutAttachmentError[] = [];
      const remainingAttachmentSlots =
        SCOUT_MAX_ATTACHMENTS - attachments.length - pendingUploads.length;
      if (remainingAttachmentSlots <= 0) {
        setAttachmentErrors([
          {
            id: `attachment-limit-${Date.now()}`,
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: `Upload is limited to ${SCOUT_MAX_ATTACHMENTS} documents per message.`,
          },
        ]);
        return;
      }
      if (!tenantId) {
        setAttachmentErrors([
          {
            id: "missing-tenant",
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: "Scout requires an organization before attaching files.",
          },
        ]);
        return;
      }
      if (files.length === 0) {
        return;
      }

      const validFiles: File[] = [];
      for (const file of files) {
        const error = validateScoutAttachmentFile(file);
        if (error) {
          nextErrors.push({
            id: `${file.name}-${file.size}-${file.lastModified}-validation`,
            filename: file.name,
            mediaType: file.type || "application/octet-stream",
            message: error,
          });
        } else {
          validFiles.push(file);
        }
      }
      if (validFiles.length === 0) {
        setAttachmentErrors(nextErrors);
        return;
      }

      const filesToUpload = validFiles.slice(0, remainingAttachmentSlots);

      if (filesToUpload.length === 0) {
        setAttachmentErrors(nextErrors);
        return;
      }

      setAttachmentErrors([]);
      setIsUploading(true);

      const uploadTasks: ScoutUploadTask[] = filesToUpload.map((file) => {
        const clientId = `${file.name}-${file.size}-${file.lastModified}-${globalThis.crypto.randomUUID()}`;
        const localFileUrl = URL.createObjectURL(file);
        const previewUrl = isScoutImageMediaType(file.type) ? localFileUrl : undefined;
        return {
          file,
          clientId,
          previewUrl,
          localFileUrl,
        };
      });

      setPendingUploads((previous) => [
        ...previous,
        ...uploadTasks.map(({ clientId, file, previewUrl, localFileUrl }) => ({
          clientId,
          filename: file.name,
          mediaType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          previewUrl,
          localFileUrl,
        })),
      ]);

      try {
        const { results, failures, totalFailures } = await runConcurrently({
          items: uploadTasks,
          maxConcurrency: uploadTasks.length,
          allSettled: true,
          task: async (task) => {
            const { file, clientId, localFileUrl } = task;
            const uploadKey = await uploadFileDirectly(file, { tenantId });
            const attachResult = await createPrivateDocument({
              variables: {
                input: {
                  tenantId,
                  uploadKey,
                  filename: file.name,
                  mediaType: file.type || "application/octet-stream",
                  sizeBytes: file.size,
                },
              },
            });
            const attached = attachResult.data?.createPrivateDocument;
            if (!attached) {
              throw new Error("Scout could not register this attachment.");
            }

            return {
              clientId,
              previewUrl: task.previewUrl,
              localFileUrl,
              attachment: {
                privateDocumentId: attached.id,
                filename: file.name,
                title: file.name,
                mediaType: attached.mediaType,
                sizeBytes: attached.sizeBytes,
                signedDownloadUrl: attached.signedDownloadUrl ?? undefined,
                thumbnailSignedUrl: attached.thumbnailSignedUrl ?? undefined,
                pageCount: attached.pageCount,
                previewUrl: persistedAttachmentPreviewUrl(task.previewUrl),
                ingestionMode: attached.ingestionMode,
                extractionStatus: attached.extractionStatus,
              } satisfies ScoutAttachment,
            };
          },
        });

        const completedAttachments = results.map((result) => result.attachment);
        const resultsByClientId = new Map(results.map((result) => [result.clientId, result]));

        if (totalFailures > 0) {
          for (const { item, error } of failures) {
            nextErrors.push({
              id: `${item.file.name}-${item.file.size}-${item.file.lastModified}-upload`,
              filename: item.file.name,
              mediaType: item.file.type || "application/octet-stream",
              message: error.message || "Scout could not upload this file.",
            });
          }
        }

        const finishedClientIds = new Set([
          ...resultsByClientId.keys(),
          ...failures.map(({ item }) => item.clientId),
        ]);
        if (finishedClientIds.size > 0) {
          setPendingUploads((previous) => {
            for (const upload of previous) {
              if (!finishedClientIds.has(upload.clientId)) {
                continue;
              }
              const result = resultsByClientId.get(upload.clientId);
              if (result) {
                revokeComposerFileResources({
                  localFileUrl: upload.localFileUrl,
                  previewUrl:
                    upload.previewUrl !== result.previewUrl ? upload.previewUrl : undefined,
                });
              } else {
                revokeComposerFileResources(upload);
              }
            }
            return previous.filter((upload) => !finishedClientIds.has(upload.clientId));
          });
        }

        if (completedAttachments.length > 0) {
          setAttachments((previous) => [...previous, ...completedAttachments]);
          onAttachmentsUploaded?.(completedAttachments);
        }

        if (nextErrors.length > 0) {
          setAttachmentErrors(nextErrors);
        }
      } catch (error) {
        setAttachmentErrors([
          ...nextErrors,
          {
            id: `upload-failed-${Date.now()}`,
            filename: "Attachment",
            mediaType: "application/octet-stream",
            message: error instanceof Error ? error.message : "Scout could not upload files.",
          },
        ]);
      } finally {
        setIsUploading(false);
      }
    },
    [
      attachments.length,
      createPrivateDocument,
      hasScoutProAccess,
      isInputLocked,
      onAttachmentsUploaded,
      pendingUploads.length,
      requestScoutProForUploads,
      tenantId,
    ]
  );

  const handleSubmit = useCallback(async () => {
    const value = inputRef.current?.value ?? "";
    const trimmedValue = value.trim();
    const attachmentPayload =
      hasScoutProAccess && attachments.length > 0 ? [...attachments] : undefined;
    if ((!trimmedValue && !attachmentPayload?.length) || isSending || isInputLocked) {
      return;
    }

    clearInput();
    clearAttachments();
    focusInput();
    try {
      await onSubmit(trimmedValue, { attachments: attachmentPayload });
    } finally {
      focusInput();
    }
  }, [
    attachments,
    clearAttachments,
    clearInput,
    focusInput,
    hasScoutProAccess,
    isInputLocked,
    isSending,
    onSubmit,
  ]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newHasContent = !!e.target.value.trim();
    setHasContent((prev) => (prev !== newHasContent ? newHasContent : prev));
  }, []);

  const handleRemoveAttachment = useCallback(
    async (privateDocumentId: string) => {
      if (isInputLocked || isSending || !tenantId || removingAttachmentIds.has(privateDocumentId)) {
        return;
      }

      const removedAttachment = attachments.find(
        (attachment) => attachment.privateDocumentId === privateDocumentId
      );
      if (!removedAttachment) {
        return;
      }

      revokeComposerFileResources(removedAttachment);
      setAttachments((previous) =>
        previous.filter((attachment) => attachment.privateDocumentId !== privateDocumentId)
      );
      setRemovingAttachmentIds((previous) => new Set(previous).add(privateDocumentId));

      try {
        const { deletedPrivateDocumentIds, failures } = await deletePrivateDocuments([
          privateDocumentId,
        ]);
        for (const deletedPrivateDocumentId of deletedPrivateDocumentIds) {
          await onAttachmentRemoved?.(deletedPrivateDocumentId);
        }
        if (failures.length > 0) {
          setAttachments((previous) => [...previous, removedAttachment]);
          setAttachmentErrors((previous) => [
            ...previous,
            ...failures.map((failure: ScoutPrivateDocumentDeleteFailure) => ({
              id: `${failure.privateDocumentId}-delete`,
              filename: removedAttachment.filename,
              mediaType: removedAttachment.mediaType,
              message: failure.message,
            })),
          ]);
        }
      } catch (error) {
        setAttachments((previous) => [...previous, removedAttachment]);
        setAttachmentErrors((previous) => [
          ...previous,
          {
            id: `${privateDocumentId}-delete`,
            filename: removedAttachment.filename,
            mediaType: removedAttachment.mediaType,
            message:
              error instanceof Error ? error.message : "Scout could not remove this attachment.",
          },
        ]);
      } finally {
        setRemovingAttachmentIds((previous) => {
          const next = new Set(previous);
          next.delete(privateDocumentId);
          return next;
        });
      }
    },
    [
      attachments,
      deletePrivateDocuments,
      isInputLocked,
      isSending,
      onAttachmentRemoved,
      removingAttachmentIds,
      tenantId,
    ]
  );

  const handleRemoveAttachmentError = useCallback(
    (errorId: string) => {
      if (isInputLocked || isSending) {
        return;
      }

      setAttachmentErrors((previous) => previous.filter((error) => error.id !== errorId));
    },
    [isInputLocked, isSending]
  );

  const handleClearAllAttachments = useCallback(async () => {
    if (
      isInputLocked ||
      isSending ||
      !tenantId ||
      attachments.length + pendingUploads.length <= 1
    ) {
      return;
    }

    for (const pendingUpload of pendingUploads) {
      revokeComposerFileResources(pendingUpload);
    }
    setPendingUploads([]);

    const attachmentsToRemove = [...attachments];
    const privateDocumentIds = attachmentsToRemove.map(
      (attachment) => attachment.privateDocumentId
    );

    setAttachments([]);
    for (const attachment of attachmentsToRemove) {
      revokeComposerFileResources(attachment);
    }
    setRemovingAttachmentIds((previous) => {
      const next = new Set(previous);
      for (const privateDocumentId of privateDocumentIds) {
        next.add(privateDocumentId);
      }
      return next;
    });

    try {
      const { deletedPrivateDocumentIds, failures } =
        await deletePrivateDocuments(privateDocumentIds);
      for (const deletedPrivateDocumentId of deletedPrivateDocumentIds) {
        await onAttachmentRemoved?.(deletedPrivateDocumentId);
      }

      if (failures.length > 0) {
        const failedIds = new Set(
          failures.map((failure: ScoutPrivateDocumentDeleteFailure) => failure.privateDocumentId)
        );
        const failedAttachments = attachmentsToRemove.filter((attachment) =>
          failedIds.has(attachment.privateDocumentId)
        );
        setAttachments(failedAttachments);
        setAttachmentErrors((previous) => [
          ...previous,
          ...failures.map((failure: ScoutPrivateDocumentDeleteFailure) => {
            const attachment = attachmentsToRemove.find(
              (candidate) => candidate.privateDocumentId === failure.privateDocumentId
            );
            return {
              id: `${failure.privateDocumentId}-delete`,
              filename: attachment?.filename ?? "Attachment",
              mediaType: attachment?.mediaType ?? "application/octet-stream",
              message: failure.message,
            };
          }),
        ]);
      }
    } catch (error) {
      setAttachments(attachmentsToRemove);
      setAttachmentErrors((previous) => [
        ...previous,
        {
          id: `clear-all-delete-${Date.now()}`,
          filename: "Attachments",
          mediaType: "application/octet-stream",
          message:
            error instanceof Error ? error.message : "Scout could not remove these attachments.",
        },
      ]);
    } finally {
      setRemovingAttachmentIds((previous) => {
        const next = new Set(previous);
        for (const privateDocumentId of privateDocumentIds) {
          next.delete(privateDocumentId);
        }
        return next;
      });
    }
  }, [
    attachments,
    deletePrivateDocuments,
    isInputLocked,
    isSending,
    onAttachmentRemoved,
    pendingUploads,
    tenantId,
  ]);

  const handleFilePickerChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!hasScoutProAccess) {
        event.currentTarget.value = "";
        requestScoutProForUploads();
        return;
      }
      if (isInputLocked) {
        event.currentTarget.value = "";
        return;
      }

      const files = event.target.files ? [...event.target.files] : [];
      event.currentTarget.value = "";
      await uploadAttachments(files);
    },
    [hasScoutProAccess, isInputLocked, requestScoutProForUploads, uploadAttachments]
  );

  const handleCanvasFileUpload = useCallback(
    async (files: File[]) => {
      if (!hasScoutProAccess) {
        requestScoutProForUploads();
        return;
      }
      if (isInputLocked || isSending) {
        return;
      }
      await uploadAttachments(files);
    },
    [hasScoutProAccess, isInputLocked, isSending, requestScoutProForUploads, uploadAttachments]
  );

  useRegisterScoutChatFileUpload(handleCanvasFileUpload);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isInputLocked) {
        return;
      }

      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        void handleSubmit();
      } else if (event.key === "ArrowUp") {
        const textarea = inputRef.current;
        // Only repopulate when the field is empty so we don't clobber what
        // the user is typing or interfere with normal arrow-key navigation
        // inside a multi-line draft.
        if (textarea && textarea.value === "" && lastUserMessage) {
          event.preventDefault();
          textarea.value = lastUserMessage;
          textarea.dispatchEvent(new Event("input", { bubbles: true }));
          setHasContent(true);
          // Place caret at end so the next keystroke appends.
          const end = lastUserMessage.length;
          textarea.setSelectionRange(end, end);
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        if (isSending && onAbort && !isAborting) {
          onAbort();
          return;
        }
        const textarea = inputRef.current;
        if (textarea && textarea.value.trim()) {
          clearInput();
          textarea.focus();
        } else if (textarea) {
          textarea.blur();
        }
      }
    },
    [clearInput, handleSubmit, isAborting, isInputLocked, isSending, lastUserMessage, onAbort]
  );

  const attachButton = (
    <Tooltip
      title={
        !hasScoutProAccess
          ? "Upload files with Scout Pro"
          : hasReachedAttachmentLimit
            ? `Attachment limit reached (${SCOUT_MAX_ATTACHMENTS})`
            : `Attach up to ${SCOUT_MAX_ATTACHMENTS} files`
      }
      arrow
    >
      <span
        style={{
          paddingRight: useExpandedComposerLayout ? 0 : 2,
          display: "inline-flex",
          cursor: isAttachButtonDisabled
            ? "default"
            : hasScoutProAccess
              ? "pointer"
              : "not-allowed",
        }}
      >
        <IconButton
          onClick={() => {
            if (hasScoutProAccess) {
              fileInputRef.current?.click();
              return;
            }
            requestScoutProForUploads();
          }}
          onMouseDown={(event) => event.preventDefault()}
          disabled={isAttachButtonDisabled}
          aria-disabled={!hasScoutProAccess || undefined}
          aria-label={
            hasScoutProAccess
              ? `Attach up to ${SCOUT_MAX_ATTACHMENTS} files`
              : "Upload files with Scout Pro"
          }
          sx={[
            ...(useExpandedComposerLayout ? [{ ml: -1 }] : []),
            ...(!hasScoutProAccess
              ? [
                  {
                    color: "action.disabled",
                    opacity: 0.72,
                    "&:hover": {
                      bgcolor: "action.hover",
                      color: "action.disabled",
                    },
                  },
                ]
              : []),
          ]}
        >
          <AttachFile sx={{ fontSize: 20 }} />
        </IconButton>
      </span>
    </Tooltip>
  );

  const sendOrStopButton =
    isSending && onAbort ? (
      <Tooltip title={isAborting ? "Stopping..." : "Stop generating (Esc)"} arrow>
        <span
          style={{
            display: "inline-flex",
            cursor: isAborting ? "default" : "pointer",
          }}
        >
          <IconButton
            onClick={onAbort}
            aria-label="Stop generating"
            color="primary"
            disabled={isAborting}
          >
            <StopIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>
    ) : (
      <Tooltip title="Send (Enter · Shift+Enter for new line)" arrow>
        <span
          style={{
            display: "inline-flex",
            cursor: isSubmitDisabled ? "default" : "pointer",
          }}
        >
          <IconButton
            onClick={() => {
              void handleSubmit();
            }}
            onMouseDown={(event) => event.preventDefault()}
            disabled={isSubmitDisabled}
            aria-label="Send message"
            color="primary"
          >
            <ArrowUpwardIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </span>
      </Tooltip>
    );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 920,
        mx: "auto",
        px: 2,
        py: 1.25,
        borderRadius: (theme) => theme.spacing(3),
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {hasScoutProAccess ? (
        <input
          ref={fileInputRef}
          type="file"
          accept={SCOUT_ATTACHMENT_FILE_INPUT_ACCEPT}
          multiple
          disabled={isAttachmentActionDisabled}
          onChange={handleFilePickerChange}
          style={{ display: "none" }}
        />
      ) : null}
      {useExpandedComposerLayout ? (
        <Box sx={{ maxHeight: 200, overflow: "auto" }}>
          {(attachments.length > 0 || pendingUploads.length > 0) && (
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              {attachments.map((attachment) => (
                <ScoutFileThumbnailCard
                  key={attachment.privateDocumentId}
                  filename={attachment.filename}
                  mediaType={attachment.mediaType}
                  sizeBytes={attachment.sizeBytes}
                  thumbnailUrl={resolveScoutAttachmentThumbnailUrl(attachment)}
                  previewFileUrl={attachment.signedDownloadUrl}
                  signedDownloadUrl={attachment.signedDownloadUrl}
                  onRemove={
                    isInputLocked
                      ? undefined
                      : () => handleRemoveAttachment(attachment.privateDocumentId)
                  }
                />
              ))}
              {pendingUploads.map((pendingUpload) => (
                <ScoutFileThumbnailCard
                  key={pendingUpload.clientId}
                  filename={pendingUpload.filename}
                  mediaType={pendingUpload.mediaType}
                  sizeBytes={pendingUpload.sizeBytes}
                  thumbnailUrl={pendingUpload.previewUrl}
                  previewFileUrl={pendingUpload.localFileUrl}
                  isLoading={true}
                />
              ))}
            </Stack>
          )}
          {attachmentErrors.length > 0 && (
            <Stack spacing={1} sx={{ mb: 1 }}>
              {attachmentErrors.map((attachmentError) => {
                const errorPart: ScoutFilePart = {
                  id: attachmentError.id,
                  type: "file",
                  title: attachmentError.filename,
                  filename: attachmentError.filename,
                  mediaType: attachmentError.mediaType,
                };
                return (
                  <Stack key={attachmentError.id} direction="row">
                    <Box sx={{ flex: 0.5 }}>
                      <ScoutFileDisplay
                        part={errorPart}
                        state="error"
                        errorText={attachmentError.message}
                        onRemove={
                          isInputLocked
                            ? undefined
                            : () => handleRemoveAttachmentError(attachmentError.id)
                        }
                      />
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>
      ) : null}

      {showScoutProUploadUpsell && !hasScoutProAccess ? (
        <Stack
          role="status"
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{
            mb: 1,
            px: 1.25,
            py: 1,
            borderLeft: 3,
            borderColor: "primary.main",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          }}
        >
          <Box sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.45 }}>
            <Box sx={{ color: "text.primary", fontWeight: 600 }}>
              File upload is included with Scout Pro.
            </Box>
            Scout Regulatory Advisor can still answer from Encamp knowledge without analyzing files.
          </Box>
          <Stack direction="row" spacing={0.5} justifyContent={{ xs: "flex-end", sm: "initial" }}>
            <Button
              href={SCOUT_SUPPORT_ARTICLE}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="text"
              onClick={handleFileUploadUpsellLearnMoreClick}
            >
              Learn More
            </Button>
            <Tooltip title="Close">
              <IconButton
                size="small"
                aria-label="Close Scout Pro file upload message"
                onClick={() => setShowScoutProUploadUpsell(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      ) : null}

      <TextField
        multiline
        minRows={1}
        maxRows={8}
        fullWidth
        variant="standard"
        slotProps={{
          input: {
            disableUnderline: true,
            ...(useExpandedComposerLayout
              ? {}
              : {
                  startAdornment: attachButton,
                  endAdornment: (
                    <>
                      {isWriteAccessFeatureEnabled && onWriteModeChange ? (
                        <ScoutWriteAccessIndicator
                          writeMode={writeMode}
                          onWriteModeChange={onWriteModeChange}
                          disabled={isSending || isInputLocked}
                        />
                      ) : null}
                      {sendOrStopButton}
                    </>
                  ),
                }),
          },
          htmlInput: { "aria-label": "Message Scout" },
        }}
        onChange={handleChange}
        placeholder={resolvedPlaceholder}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
        sx={{
          "& .MuiInputBase-inputMultiline": {
            padding: "4px 0",
            ...(useExpandedComposerLayout
              ? {}
              : { paddingRight: `${inputActionRightPaddingPx}px` }),
            minHeight: 24,
            lineHeight: 1.5,
            overflowX: "hidden",
            overflowY: "auto",
            scrollbarGutter: "stable",
            scrollbarWidth: "thin",
            scrollbarColor: `${inputScrollbarThumbColor} ${inputSurfaceColor}`,
            "&::-webkit-scrollbar": {
              width: `${INPUT_SCROLLBAR_WIDTH_PX}px`,
              backgroundColor: inputSurfaceColor,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: inputSurfaceColor,
              border: "none",
              boxShadow: "none",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: inputScrollbarThumbColor,
              border: "none",
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: inputSurfaceColor,
            },
          },
        }}
      />

      {useExpandedComposerLayout ? (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="center">
            {attachButton}
            {showClearAllAttachments ? (
              <Button
                variant="text"
                size="small"
                disabled={isInputLocked}
                onClick={handleClearAllAttachments}
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  minWidth: "auto",
                  px: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Clear All
              </Button>
            ) : null}
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0}>
            {isWriteAccessFeatureEnabled && onWriteModeChange ? (
              <ScoutWriteAccessIndicator
                writeMode={writeMode}
                onWriteModeChange={onWriteModeChange}
                disabled={isSending || isInputLocked}
              />
            ) : null}
            {sendOrStopButton}
          </Stack>
        </Stack>
      ) : null}

      {composerFooter ? (
        <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
          {composerFooter}
        </Stack>
      ) : null}
    </Box>
  );
});
ScoutInput.displayName = "ScoutThreadInput";
